import React, { useState } from 'react';
import { Loader2, Redo2, RotateCcw, Undo2, Wand2 } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { claudeQueryService } from '../../../../../services/claudeQueryService';
import { ScanTextInput } from '../../../../common/inputs';
import { isViewSpec, type DatasetMap, type ViewSpec } from './types';

interface ViewAdjustBarProps {
    view: ViewSpec;
    datasets: DatasetMap;
    /** SQL per datasett-id (kreves for å justere — serveren kjører SQL-en på nytt for profilen) */
    datasetSql: Record<string, string>;
    /** Rapporten visningen tilhører (rapportvisning) */
    reportId?: number;
    onAdjusted: (view: ViewSpec, datasets?: DatasetMap) => void;
    /** Ønsket krever nye data: chat sender det som et nytt spørsmål */
    onNeedsDataChange?: (instruction: string, reason?: string) => void;
    canUndo: boolean;
    canRedo: boolean;
    isModified: boolean;
    onUndo: () => void;
    onRedo: () => void;
    onReset: () => void;
}

/**
 * Justering av en Compass-visning: fritekst («vis per uke», «legg inn mållinje på 1200», «flytt KPI-ene øverst»)
 * sendes til Compass (Haiku, få tokens). Angre/gjør om/tilbakestill virker lokalt uten tokens.
 * Store trykkflater (≥ 44 px) — brukes på nettbrett.
 */
export const ViewAdjustBar: React.FC<ViewAdjustBarProps> = ({
    view, datasetSql, reportId, onAdjusted, onNeedsDataChange, canUndo, canRedo, isModified, onUndo, onRedo, onReset
}) => {
    const { t } = useTranslation();
    const [instruction, setInstruction] = useState('');
    const [panelId, setPanelId] = useState('');
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState<string | null>(null);

    const submit = async () => {
        const text = instruction.trim();
        if (!text || busy) return;
        const refs = view.datasets
            .map(d => ({ id: d.id, sql: datasetSql[d.id] }))
            .filter((d): d is { id: string; sql: string } => !!d.sql);
        if (refs.length === 0) {
            setNote(t('manager.modules.claudeQuery.view.adjustNoSql', {}, 'Visningen mangler SQL og kan ikke justeres.'));
            return;
        }
        setBusy(true);
        setNote(null);
        try {
            const result = await claudeQueryService.adjustView({ view, datasets: refs, instruction: text, panelId: panelId || undefined, reportId });
            if (result.needsDataChange) {
                if (onNeedsDataChange) onNeedsDataChange(text, result.dataChangeReason);
                else setNote(t('manager.modules.claudeQuery.view.adjustNeedsData', { '0': result.dataChangeReason ?? '' },
                    `Dette krever andre data (${result.dataChangeReason ?? ''}). Still spørsmålet i chatten.`));
            } else if (result.success && isViewSpec(result.view)) {
                onAdjusted(result.view, result.datasets as DatasetMap | undefined);
                setNote(result.message || null);
                setInstruction('');
            } else {
                setNote(result.errorMessage || t('manager.modules.claudeQuery.view.adjustFailed', {}, 'Compass klarte ikke å justere visningen.'));
            }
        } catch (err) {
            setNote(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const iconButton = 'flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40';

    return (
        <div className="mt-3 border-t border-gray-100 pt-3">
            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={panelId}
                    onChange={e => setPanelId(e.target.value)}
                    className="min-h-[44px] px-2 border border-gray-200 rounded-lg text-sm bg-white max-w-[14rem]"
                    aria-label={t('manager.modules.claudeQuery.view.adjustPanel', {}, 'Gjelder')}
                >
                    <option value="">{t('manager.modules.claudeQuery.view.adjustWholeView', {}, 'Hele visningen')}</option>
                    {view.panels.map(p => <option key={p.id} value={p.id}>{p.title || p.id}</option>)}
                </select>
                <ScanTextInput
                    value={instruction}
                    onChange={e => setInstruction(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                    placeholder={t('manager.modules.claudeQuery.view.adjustPlaceholder', {}, 'Juster visningen, f.eks. «vis per uke» eller «legg inn mållinje på 1200»')}
                    className="flex-1 min-w-[14rem] min-h-[44px] px-3 border border-gray-200 rounded-lg text-sm"
                    disabled={busy}
                />
                <button
                    onClick={submit}
                    disabled={busy || !instruction.trim()}
                    className="flex items-center gap-1 min-h-[44px] px-4 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                    {t('manager.modules.claudeQuery.view.adjust', {}, 'Juster')}
                </button>
                <button onClick={onUndo} disabled={!canUndo || busy} className={iconButton} aria-label={t('manager.modules.claudeQuery.view.undo', {}, 'Angre')}>
                    <Undo2 size={16} />
                </button>
                <button onClick={onRedo} disabled={!canRedo || busy} className={iconButton} aria-label={t('manager.modules.claudeQuery.view.redo', {}, 'Gjør om')}>
                    <Redo2 size={16} />
                </button>
                <button onClick={onReset} disabled={!isModified || busy} className={iconButton} aria-label={t('manager.modules.claudeQuery.view.reset', {}, 'Tilbakestill til Compass-forslaget')}>
                    <RotateCcw size={16} />
                </button>
            </div>
            {/* Alltid synlige merknader i stedet for hover-tooltips (nettbrett) */}
            <p className="text-xs text-gray-400 mt-1">
                {t('manager.modules.claudeQuery.view.adjustHint', {}, 'Angre, gjør om og tilbakestill bruker ikke Compass. Justering bruker noen få tokens.')}
            </p>
            {note && <p className="text-xs text-gray-600 mt-1">{note}</p>}
        </div>
    );
};
